import * as THREE from 'three';
import { NoiseField, lerp } from './noise';

export type SharedUniforms = ReturnType<typeof createSharedUniforms>;

function createSharedUniforms(noiseTexture: THREE.Texture) {
  return {
    uTime: { value: 0 },
    uSunDir: { value: new THREE.Vector3(0, 0.3, -1).normalize() },
    uSunColor: { value: new THREE.Color() },
    uSkyZenith: { value: new THREE.Color() },
    uSkyHorizon: { value: new THREE.Color() },
    uSkyHorizonAway: { value: new THREE.Color() },
    uHazeColor: { value: new THREE.Color() },
    uSkyAmbient: { value: new THREE.Color() },
    uGroundAmbient: { value: new THREE.Color() },
    uTransTint: { value: new THREE.Color(1.0, 0.76, 0.36) },
    uFogDensity: { value: 0.00055 },
    uFogFalloff: { value: 0.011 },
    uNoise: { value: noiseTexture },
    uWindDir: { value: new THREE.Vector2(1, 0) },
    uWindStrength: { value: 0.6 },
    uGustOffsetA: { value: new THREE.Vector2() },
    uGustOffsetB: { value: new THREE.Vector2() },
    uCloudOffset: { value: new THREE.Vector2() },
    uCloudCoverage: { value: 0.5 },
    uCloudShadow: { value: 0.32 },
    uCapsuleA: { value: new THREE.Vector4(0, 0, 0, 0.22) },
    uCapsuleB: { value: new THREE.Vector4(0, 1.4, 0, 0) },
    uHatSphere: { value: new THREE.Vector4(0, 1.75, 0, 0.3) },
  };
}

type Palette = {
  elevation: number;
  azimuth: number;
  sun: [number, number, number];
  sunIntensity: number;
  zenith: [number, number, number];
  horizon: [number, number, number];
  away: [number, number, number];
  haze: [number, number, number];
  fog: number;
  coverage: number;
};

// Golden afternoon -> late golden hour -> sunset. Driven by shrines lit.
const PALETTES: Palette[] = [
  {
    elevation: 14,
    azimuth: 8,
    sun: [1.0, 0.84, 0.62],
    sunIntensity: 5.2,
    zenith: [0.3, 0.385, 0.52],
    horizon: [1.04, 0.95, 0.72],
    away: [0.66, 0.7, 0.76],
    haze: [0.78, 0.68, 0.52],
    fog: 0.00048,
    coverage: 0.46,
  },
  {
    elevation: 9,
    azimuth: 10,
    sun: [1.0, 0.74, 0.47],
    sunIntensity: 4.8,
    zenith: [0.34, 0.39, 0.52],
    horizon: [1.06, 0.88, 0.58],
    away: [0.7, 0.66, 0.68],
    haze: [0.76, 0.6, 0.44],
    fog: 0.00068,
    coverage: 0.52,
  },
  {
    elevation: 3.5,
    azimuth: 12,
    sun: [1.0, 0.56, 0.3],
    sunIntensity: 4.2,
    zenith: [0.3, 0.31, 0.44],
    horizon: [1.08, 0.7, 0.46],
    away: [0.56, 0.5, 0.56],
    haze: [0.7, 0.48, 0.34],
    fog: 0.0008,
    coverage: 0.55,
  },
];

const tmpA = new THREE.Color();
const tmpB = new THREE.Color();

export class Environment {
  readonly noise: NoiseField;
  readonly noiseTexture: THREE.DataTexture;
  readonly uniforms: SharedUniforms;
  readonly sunDirection = new THREE.Vector3();
  /** Brightness of the sun light for standard materials (character, props). */
  sunLightIntensity = 3;
  dayProgress = 0;
  private displayedProgress = 0;

  constructor(seed = 7) {
    this.noise = new NoiseField(seed);
    this.noiseTexture = new THREE.DataTexture(this.noise.data, this.noise.size, this.noise.size, THREE.RGBAFormat);
    this.noiseTexture.wrapS = THREE.RepeatWrapping;
    this.noiseTexture.wrapT = THREE.RepeatWrapping;
    this.noiseTexture.magFilter = THREE.LinearFilter;
    this.noiseTexture.minFilter = THREE.LinearMipmapLinearFilter;
    this.noiseTexture.generateMipmaps = true;
    this.noiseTexture.needsUpdate = true;
    this.uniforms = createSharedUniforms(this.noiseTexture);
    this.applyPalette(0);
  }

  setDayProgress(progress: number, immediate = false): void {
    this.dayProgress = THREE.MathUtils.clamp(progress, 0, 1);
    if (immediate) {
      this.displayedProgress = this.dayProgress;
      this.applyPalette(this.displayedProgress);
    }
  }

  /** Returns true when lighting changed enough that the environment map should be refreshed. */
  update(dt: number): boolean {
    const diff = this.dayProgress - this.displayedProgress;
    if (Math.abs(diff) < 1e-4) return false;
    this.displayedProgress += Math.sign(diff) * Math.min(Math.abs(diff), dt * 0.035);
    this.applyPalette(this.displayedProgress);
    return true;
  }

  get displayed(): number {
    return this.displayedProgress;
  }

  private applyPalette(progress: number): void {
    const scaled = progress * (PALETTES.length - 1);
    const index = Math.min(Math.floor(scaled), PALETTES.length - 2);
    const t = scaled - index;
    const a = PALETTES[index];
    const b = PALETTES[index + 1];
    const u = this.uniforms;

    const elevation = THREE.MathUtils.degToRad(lerp(a.elevation, b.elevation, t));
    const azimuth = THREE.MathUtils.degToRad(lerp(a.azimuth, b.azimuth, t));
    this.sunDirection.set(
      Math.sin(azimuth) * Math.cos(elevation),
      Math.sin(elevation),
      -Math.cos(azimuth) * Math.cos(elevation),
    );
    u.uSunDir.value.copy(this.sunDirection);

    const intensity = lerp(a.sunIntensity, b.sunIntensity, t);
    u.uSunColor.value.copy(tmpA.fromArray(a.sun).lerp(tmpB.fromArray(b.sun), t)).multiplyScalar(intensity);
    u.uSkyZenith.value.copy(tmpA.fromArray(a.zenith).lerp(tmpB.fromArray(b.zenith), t));
    u.uSkyHorizon.value.copy(tmpA.fromArray(a.horizon).lerp(tmpB.fromArray(b.horizon), t));
    u.uSkyHorizonAway.value.copy(tmpA.fromArray(a.away).lerp(tmpB.fromArray(b.away), t));
    u.uHazeColor.value.copy(tmpA.fromArray(a.haze).lerp(tmpB.fromArray(b.haze), t));
    u.uFogDensity.value = lerp(a.fog, b.fog, t);
    u.uCloudCoverage.value = lerp(a.coverage, b.coverage, t);

    // Ambient terms approximate the sky dome above and warm bounce from the field below.
    u.uSkyAmbient.value.copy(u.uSkyZenith.value).lerp(u.uSkyHorizon.value, 0.5).multiplyScalar(0.62);
    u.uGroundAmbient.value.setRGB(0.5, 0.33, 0.14).multiplyScalar(0.16 * intensity * Math.max(Math.sin(elevation), 0.1) * 2.2);
    this.sunLightIntensity = intensity * 0.62;
  }
}
